import { useEffect } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Phone } from "./Phone";
import { Reveal } from "./shared";
import { FINAL_SCRIPT } from "../../data/content";

const GHL_FORM_URL = "https://api.leadconnectorhq.com/widget/form/40NlpgpMDopSAXKltssW";

export default function FinalCta() {
    useEffect(() => {
        const s = document.createElement("script");
        s.src = "https://link.msgsndr.com/js/form_embed.js";
        s.async = true;
        document.body.appendChild(s);
        return () => document.body.removeChild(s);
    }, []);

    return (
        <section id="book" className="py-24 sm:py-32 relative overflow-hidden" data-testid="final-cta-section">
            <div className="absolute inset-0 bg-grid opacity-60" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-cyan-500/[0.09] blur-[160px] rounded-full pointer-events-none" />

            <div className="container-x relative">
                <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                    <div>
                        <Reveal>
                            <p className="ff-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">12 — Final Call</p>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white" data-testid="final-cta-headline">
                                Stop Letting Good Leads <span className="text-gradient">Go Cold.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-lg">
                                Your next customer could be texting you right now.
                            </p>
                        </Reveal>
                        <Reveal delay={0.24}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <a href="#audit-form" className="btn-primary" data-testid="final-cta-audit-button">
                                    Book My Free Automation Audit
                                    <ArrowRight className="w-4 h-4" />
                                </a>
                                <a href="#demo" className="btn-ghost" data-testid="final-cta-demo-button">
                                    See the AI in Action
                                </a>
                            </div>
                        </Reveal>
                        <Reveal delay={0.32}>
                            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
                                {["Free 15-minute audit", "No technical work required", "Live in days, not months"].map((t) => (
                                    <span key={t} className="flex items-center gap-2 text-sm text-slate-400">
                                        <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2} className="hidden lg:flex justify-center">
                        <div style={{ perspective: 1200 }}>
                            <div style={{ transform: "rotateX(3deg) rotateY(-6deg) scale(0.85)", transformOrigin: "center" }}>
                                <Phone script={FINAL_SCRIPT} headerTitle="New Lead" testid="final-cta-phone" />
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal>
                    <div id="audit-form" className="glass rounded-[2rem] p-6 sm:p-10 max-w-3xl mx-auto" data-testid="audit-form-container">
                        <div className="text-center mb-8">
                            <h3 className="ff-display text-2xl sm:text-3xl font-bold text-white tracking-tight">Show Me What I Can Automate</h3>
                            <p className="mt-3 text-sm text-slate-400 max-w-md mx-auto">
                                Tell us about your business and we'll show you exactly how many leads you're missing — and how to catch them.
                            </p>
                        </div>
                        <div className="rounded-2xl overflow-hidden bg-white" data-testid="gohighlevel-iframe-container">
                            <iframe
                                src={GHL_FORM_URL}
                                style={{ width: "100%", height: "620px", border: "none" }}
                                id="inline-40NlpgpMDopSAXKltssW"
                                title="Book a Free Automation Audit"
                                data-layout="{'id':'INLINE'}"
                                data-trigger-type="alwaysShow"
                                data-activation-type="alwaysActivated"
                                data-deactivation-type="neverDeactivate"
                                data-form-name="Automation Audit Form"
                                data-height="620"
                                data-layout-iframe-id="inline-40NlpgpMDopSAXKltssW"
                                data-form-id="40NlpgpMDopSAXKltssW"
                                data-testid="ghl-form-iframe"
                            />
                        </div>
                        <p className="mt-6 text-[11px] text-slate-500 leading-relaxed text-center max-w-xl mx-auto" data-testid="form-consent-note">
                            By submitting this form, you agree to receive SMS and email from Flowtech about your enquiry.
                            Message &amp; data rates may apply. Reply STOP to opt out anytime. See our{" "}
                            <a href="/privacy-policy" className="text-cyan-300/80 hover:text-cyan-200 underline underline-offset-2 transition-colors duration-300">
                                Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a href="/terms-and-conditions" className="text-cyan-300/80 hover:text-cyan-200 underline underline-offset-2 transition-colors duration-300">
                                Terms &amp; Conditions
                            </a>
                            .
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
